import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, setSessionExpiredHandler, tokenStore } from '../api/client';
import type { Customer } from '../api/types';

interface AuthState {
  customer: Customer | null;
  /** True until the stored token has been checked on first load. */
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (input: {
    email: string;
    password: string;
    fullName: string;
    phone: string;
    marketingOptIn: boolean;
    notifyBySms: boolean;
  }) => Promise<void>;
  logout: () => Promise<void>;
  /** Adopts a session created outside the login form — the password reset does this. */
  adoptSession: (customer: Customer) => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);

  // Restore the session from the stored token so a refresh keeps you signed in.
  useEffect(() => {
    let cancelled = false;

    async function restore() {
      if (!tokenStore.access) {
        setLoading(false);
        return;
      }

      try {
        const me = await api.me();

        if (!cancelled) {
          setCustomer(me);
        }
      } catch {
        tokenStore.clear();
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void restore();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setSessionExpiredHandler(() => setCustomer(null));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const auth = await api.login(email, password);
    tokenStore.save(auth);
    setCustomer(auth.customer);
  }, []);

  const register = useCallback(async (input: Parameters<AuthState['register']>[0]) => {
    const auth = await api.register(input);
    tokenStore.save(auth);
    setCustomer(auth.customer);
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = tokenStore.refresh;

    if (refreshToken) {
      // Best effort — the local session is cleared either way.
      await api.logout(refreshToken).catch(() => undefined);
    }

    tokenStore.clear();
    setCustomer(null);
  }, []);

  const adoptSession = useCallback((next: Customer) => setCustomer(next), []);

  const value = useMemo(
    () => ({ customer, loading, login, register, logout, adoptSession }),
    [customer, loading, login, register, logout, adoptSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }

  return context;
}
